import React, { useState, useRef } from 'react';

let makeRowId = 1000;
const makeAmountRow = (amount = '', description = '') => ({
  id: makeRowId++,
  amount,
  description,
});

const DonationSettingsSection = ({
  allFrequencies, enabledFrequencies, setEnabledFrequencies,
  amounts, setAmounts,
  showOtherAmount, setShowOtherAmount,
}) => {
  const [activeFreqTab, setActiveFreqTab] = useState(enabledFrequencies[0] || 'one_time');
  const draggedId = useRef(null);
  const [draggingId, setDraggingId] = useState(null);

  const toggleFrequency = (freq) => {
    setEnabledFrequencies(prev =>
      prev.includes(freq)
        ? prev.filter(f => f !== freq)
        : [...prev, freq]
    );
  };

  const updateAmountRow = (index, field, value) => {
    setAmounts(prev => prev.map((row, i) => i === index ? { ...row, [field]: value } : row));
  };
  const removeAmountRow = (index) => {
    setAmounts(prev => prev.filter((_, i) => i !== index));
  };
  const addAmountRow = () => {
    setAmounts(prev => [...prev, makeAmountRow()]);
  };

  const handleDragStart = (e, row) => {
    draggedId.current = row.id;
    setDraggingId(row.id);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', '');
    if (e.target && e.target.style) {
      requestAnimationFrame(() => { e.target.style.opacity = '0.4'; });
    }
  };
  const handleDragOver = (e, targetRow) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (!draggedId.current || draggedId.current === targetRow.id) return;

    setAmounts(prev => {
      const fromIdx = prev.findIndex(r => r.id === draggedId.current);
      const toIdx = prev.findIndex(r => r.id === targetRow.id);
      if (fromIdx === -1 || toIdx === -1 || fromIdx === toIdx) return prev;

      const next = [...prev];
      const [moved] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, moved);
      return next;
    });
  };
  const handleDrop = (e) => { e.preventDefault(); };
  const handleDragEnd = (e) => {
    if (e.target && e.target.style) e.target.style.opacity = '';
    draggedId.current = null;
    setDraggingId(null);
  };

  return (
    <div className="gc-section">
      <div className="gc-section-row">
        <div className="gc-section-info">
          <h3 className="gc-section-title">Donation Settings</h3>
          <p className="gc-section-desc">Configure the amounts and frequencies donors can choose.</p>
        </div>
        <div className="gc-section-fields">

          <div className="gc-freq-tabs gc-builder-freq-tabs">
            {allFrequencies.map(freq => (
              <button
                key={freq.value}
                type="button"
                className={`gc-freq-tab${activeFreqTab === freq.value ? ' gc-freq-tab--active' : ''}`}
                onClick={() => setActiveFreqTab(freq.value)}
              >
                <input
                  type="checkbox"
                  className="gc-freq-check"
                  checked={enabledFrequencies.includes(freq.value)}
                  onChange={(e) => { e.stopPropagation(); toggleFrequency(freq.value); }}
                  onClick={(e) => e.stopPropagation()}
                />
                {freq.label}
              </button>
            ))}
          </div>

          {!enabledFrequencies.includes(activeFreqTab) && (
            <div className="gc-amounts-disabled">
              This frequency is disabled. Check the box above to enable it.
            </div>
          )}

          <div className={`gc-amounts-table${!enabledFrequencies.includes(activeFreqTab) ? ' gc-amounts-table--disabled' : ''}`}>
            {amounts.map((row, i) => (
              <div
                key={row.id}
                className={`gc-amounts-row${draggingId === row.id ? ' gc-amounts-row--dragging' : ''}`}
                draggable
                onDragStart={(e) => handleDragStart(e, row)}
                onDragOver={(e) => handleDragOver(e, row)}
                onDrop={handleDrop}
                onDragEnd={handleDragEnd}
              >
                <div className="gc-amounts-col-handle" title="Drag to reorder">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                    <circle cx="5" cy="3" r="1.5" /><circle cx="11" cy="3" r="1.5" />
                    <circle cx="5" cy="8" r="1.5" /><circle cx="11" cy="8" r="1.5" />
                    <circle cx="5" cy="13" r="1.5" /><circle cx="11" cy="13" r="1.5" />
                  </svg>
                </div>
                <div className="gc-amounts-col-amount">
                  <div className="gc-input-group">
                    <span className="gc-input-addon">$</span>
                    <input
                      type="number"
                      className="gc-input"
                      value={row.amount}
                      onChange={(e) => updateAmountRow(i, 'amount', e.target.value)}
                      min="1"
                      step="1"
                    />
                  </div>
                </div>
                <div className="gc-amounts-col-desc">
                  <input
                    type="text"
                    className="gc-input"
                    placeholder="Description (optional)"
                    value={row.description}
                    onChange={(e) => updateAmountRow(i, 'description', e.target.value)}
                    maxLength={50}
                  />
                </div>
                <div className="gc-amounts-col-action">
                  <button type="button" className="gc-amounts-delete" onClick={() => removeAmountRow(i)} title="Remove amount">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 6H5H21" /><path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>

          {enabledFrequencies.includes(activeFreqTab) && (
            <button type="button" className="gc-amounts-add" onClick={addAmountRow}>
              + Add suggested amount
            </button>
          )}

          <div className="gc-amounts-options">
            <label className="gc-checkbox-label">
              <input
                type="checkbox"
                className="gc-checkbox"
                checked={showOtherAmount}
                onChange={() => setShowOtherAmount(!showOtherAmount)}
              />
              Show an "Other" option to allow supporters to enter their own amount
            </label>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DonationSettingsSection;
